"use client";

import { createSupabaseBrowserClient } from "@/lib/supabase/browser";
import { Loader2 } from "lucide-react";
import { createGoogleSignInButtonStore } from "@/stores/auth-form-stores";
import { useScopedStore } from "@/stores/scoped-store";

interface GoogleSignInButtonProps {
  redirectTo?: string;
  className?: string;
  label?: string;
  variant?: "default" | "dark";
}

export function GoogleSignInButton({
  redirectTo,
  className = "",
  label = "Continue with Google",
  variant = "default"
}: GoogleSignInButtonProps) {
  const { isLoading, error, setIsLoading, setError } = useScopedStore(
    `google-sign-in:${variant}:${label}:${redirectTo ?? "auto"}`,
    createGoogleSignInButtonStore,
    (state) => state
  );

  const handleGoogleSignIn = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const supabase = createSupabaseBrowserClient();
      
      const origin = typeof window !== "undefined" ? window.location.origin : "";
      // If we are on an auth page, redirect to /account, else redirect back to current page.
      const currentPath = typeof window !== "undefined" ? window.location.pathname + window.location.search : "";
      const fallbackRedirect = currentPath.startsWith("/auth") ? "/account" : currentPath;
      const next = redirectTo || fallbackRedirect || "/account";
      
      const callbackUrl = `${origin}/auth/callback?next=${encodeURIComponent(next)}`;

      const { error: signInError } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: callbackUrl,
          queryParams: {
            access_type: "offline",
            prompt: "consent",
          },
        },
      });

      if (signInError) throw signInError;
    } catch (err) {
      console.error("Google sign in failed:", err);
      setError(err instanceof Error ? err.message : "Failed to initialize Google Sign In.");
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full">
      <button
        type="button"
        disabled={isLoading}
        onClick={handleGoogleSignIn}
        className={
          variant === "dark"
            ? `auth-action inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-md border border-white/15 bg-[#050505] px-5 py-2.5 text-sm font-black uppercase tracking-wider text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.12),0_14px_32px_rgba(0,0,0,0.42)] transition-all hover:bg-[#111111] disabled:pointer-events-none disabled:opacity-60 ${className}`
            : `auth-action nv-button-secondary w-full ${className}`
        }
      >
        {isLoading ? (
          <Loader2 className="h-5 w-5 animate-spin text-current" />
        ) : (
          <svg className="h-5 w-5 shrink-0 mr-1" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              fill="#4285F4"
            />
            <path
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              fill="#34A853"
            />
            <path
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z"
              fill="#FBBC05"
            />
            <path
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"
              fill="#EA4335"
            />
          </svg>
        )}
        <span>{label}</span>
      </button>
      {error && (
        <p className="mt-2 text-center text-xs font-semibold text-rose-400">
          {error}
        </p>
      )}
    </div>
  );
}
